import AddIcon from "@mui/icons-material/Add";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createBroker,
  deleteBroker,
  getBrokers,
  updateBroker,
} from "../../services/portfolioService";
import { useCurrency } from "../Auth/AuthContext";

const BrokersPage = () => {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    account_number: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // NEW: Currency conversion
  const {
    selectedCurrency,
    formatMoney,
    convert,
    userCurrency,
    convertAndFormat,
  } = useCurrency();

  useEffect(() => {
    fetchBrokers();
  }, []);

  const fetchBrokers = async () => {
    try {
      setLoading(true);
      const data = await getBrokers();
      setBrokers(data);
    } catch (error) {
      console.error("Error fetching brokers:", error);
      setError("Failed to load brokers");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBroker = async () => {
    if (!formData.name.trim()) {
      setError("Broker name is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await createBroker(formData);
      setCreateDialogOpen(false);
      setFormData({ name: "", account_number: "", notes: "" });
      await fetchBrokers();
    } catch (error) {
      console.error("Error creating broker:", error);
      setError(error.response?.data?.message || "Failed to create broker");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateBroker = async () => {
    if (!formData.name.trim()) {
      setError("Broker name is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await updateBroker(selectedBroker.id, formData);
      setEditDialogOpen(false);
      setSelectedBroker(null);
      setFormData({ name: "", account_number: "", notes: "" });
      await fetchBrokers();
    } catch (error) {
      console.error("Error updating broker:", error);
      setError(error.response?.data?.message || "Failed to update broker");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBroker = async () => {
    try {
      setSubmitting(true);
      setError(null);
      await deleteBroker(selectedBroker.id);
      setDeleteDialogOpen(false);
      setSelectedBroker(null);
      await fetchBrokers();
    } catch (error) {
      console.error("Error deleting broker:", error);
      setError(
        error.response?.data?.message ||
          "Failed to delete broker. Make sure there are no holdings in this broker.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (broker) => {
    setSelectedBroker(broker);
    setFormData({
      name: broker.name,
      account_number: broker.account_number || "",
      notes: broker.notes || "",
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (broker) => {
    setSelectedBroker(broker);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5" fontWeight={600}>
            <BusinessCenterIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Brokers
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add Broker
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {brokers.length === 0 ? (
          <Box textAlign="center" py={6}>
            <BusinessCenterIcon
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No brokers yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Add your first broker to start tracking your portfolio
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Add First Broker
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {brokers.map((broker) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={broker.id + broker.main_currency}
              >
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <Typography variant="h6" fontWeight={600}>
                        {broker.name}
                      </Typography>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(broker)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(broker)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {broker.account_number && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Account: {broker.account_number}
                      </Typography>
                    )}

                    <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                      <Typography variant="body2" color="text.secondary">
                        Holdings: <strong>{broker.holdings_count}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Value:{" "}
                        <strong>
                          {convertAndFormat(
                            broker.total_value,
                            broker.main_currency,
                          )}
                        </strong>
                      </Typography>
                      {selectedCurrency !== "DEFAULT" &&
                        broker.main_currency !== selectedCurrency && (
                          <Typography variant="caption" color="text.secondary">
                            (
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: broker.main_currency,
                              maximumFractionDigits: 2,
                            }).format(broker.total_value)}
                            )
                          </Typography>
                        )}
                    </Box>

                    {broker.notes && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={2}
                        sx={{ fontStyle: "italic" }}
                      >
                        {broker.notes}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Create Broker Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !submitting && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Broker</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Broker Name *"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={submitting}
            placeholder="e.g., XTB, eToro, Tradeville"
          />
          <TextField
            margin="dense"
            label="Account Number"
            fullWidth
            value={formData.account_number}
            onChange={(e) =>
              setFormData({ ...formData, account_number: e.target.value })
            }
            disabled={submitting}
            placeholder="Optional"
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            disabled={submitting}
            placeholder="Any additional notes"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateBroker}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : "Add Broker"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Broker Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !submitting && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Broker</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Broker Name *"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={submitting}
          />
          <TextField
            margin="dense"
            label="Account Number"
            fullWidth
            value={formData.account_number}
            onChange={(e) =>
              setFormData({ ...formData, account_number: e.target.value })
            }
            disabled={submitting}
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateBroker}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !submitting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Broker?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedBroker?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={2}>
            Note: You cannot delete a broker that has holdings. Please delete
            all holdings first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteBroker}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BrokersPage;
